import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { languages } from '@shared/helpers/languages.helper';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { IQuestionSet } from '@shared/interfaces/question-set.interface';
import { IReferenceRelationship } from '@shared/interfaces/reference-relationship.interface';
import { ISetting } from '@shared/interfaces/setting.interface';
import { ReferenceRelationship } from '@shared/models/reference-relationship.model';
import { LoggerService } from '@shared/services/logger.service';
import { QuestionSetsService } from '@shared/services/question-sets.service';
import { ReferenceRelationshipsService } from '@shared/services/reference-relationships.service';
import { ToastService } from '@shared/services/toast.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { SessionStorage } from 'ngx-webstorage';
import { v4 as uuid } from 'uuid';

@Component({
    selector: 'app-modals-reference-relationship-edit',
    templateUrl: './modals.reference-relationship-edit.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModalsReferenceRelationshipEditComponent implements OnInit {
  @SessionStorage() private hiringFirm: IHiringFirm;
  @SessionStorage() private settings: ISetting;
  @SessionStorage() private referenceRelationships: IReferenceRelationship[];

  public title = String('MODALS.REFERENCE_RELATIONSHIPS.EDIT_TITLE');
  public submitKey = String('BUTTONS.SAVE');

  public entry: FormGroup;
  public errors: any = [];
  public languages: any[] = [];
  public additionalLanguages: any[] = []; // Non-English languages (es, fr)
  public questionSets: any[] = [];
  public isButtonsDisabled = Boolean(false);
  public relationshipValue: string;
  public isFormReady = Boolean(false);

  // Public property that can be set by the parent
  public relationshipData: any;

  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _logger: LoggerService,
    private readonly _fb: FormBuilder,
    private readonly _referenceRelationships: ReferenceRelationshipsService,
    private readonly _questionSets: QuestionSetsService,
    private readonly _toast: ToastService,
    public bsModalRef: BsModalRef
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.initializeForm(this.relationshipData);
  }

  public initializeForm(data?: any): void {
    this.isFormReady = false;
    this.relationshipValue = data?.value || uuid().slice(0, 8);
    this.title = data?.value ? 'MODALS.REFERENCE_RELATIONSHIPS.EDIT_TITLE' : 'MODALS.REFERENCE_RELATIONSHIPS.ADD_TITLE';

    // Load question sets first, then build the form
    this.loadQuestionSets();
  }

  private buildForm(): void {
    // Find existing relationship with this value
    const existingRelationship = this.referenceRelationships.find(
      (rr: IReferenceRelationship) => rr.value === this.relationshipValue
    );

    // Get available languages
    if (existingRelationship?.relationship_languages && existingRelationship.relationship_languages.length > 0) {
      // Editing existing relationship: use languages from relationship_languages
      const languageCodes = existingRelationship.relationship_languages.map((rl: any) => rl.code);
      this.languages = languages.filter(
        (l: any) => languageCodes.indexOf(l.id) > -1
      );
      console.log('Using languages from relationship_languages:', this.languages);
    } else {
      // Creating new relationship: use languages from settings
      this.languages = languages.filter(
        (l: any) => this.settings.languages.indexOf(l.id) > -1
      );
      console.log('Using languages from settings:', this.languages);
    }

    // Ensure English is always available (as the default language)
    const hasEnglish = this.languages.some((l: any) => l.id === 'en');
    if (!hasEnglish) {
      const englishLang = languages.find((l: any) => l.id === 'en');
      if (englishLang) {
        this.languages.unshift(englishLang); // Add English at the beginning
      }
    }

    // Separate additional languages (non-English) for conditional display
    // Only show them if multi-language plugin is enabled
    this.additionalLanguages = this.settings.is_plugins_multi_languages_enabled
      ? this.languages.filter((l: any) => l.id !== 'en')
      : [];

    // Set up form with language fields
    const languageFields: any = {};
    this.languages.forEach((lang: any) => {
      let labelValue = '';

      if (existingRelationship?.relationship_languages) {
        // New structure: get label from relationship_languages array
        const langData = existingRelationship.relationship_languages.find(
          (rl: any) => rl.code === lang.id
        );
        labelValue = langData?.label || '';
        console.log(`Setting form field [${lang.id}] = "${labelValue}" (from relationship_language)`, langData);
      } else {
        // Legacy structure: find relationship for this language
        const legacy = this.referenceRelationships.find(
          (rr: IReferenceRelationship) =>
            rr.value === this.relationshipValue && rr.language === lang.id
        );
        labelValue = legacy?.label || '';
        console.log(`Setting form field [${lang.id}] = "${labelValue}" (from legacy)`);
      }

      languageFields[lang.id] = [labelValue, []];
    });

    const questionSetId = existingRelationship?.question_set?.id || '';

    this.entry = this._fb.group({
      ...languageFields,
      question_set_id: [questionSetId, []],
      prevent_personal_emails: [
        existingRelationship?.prevent_personal_emails || false,
        []
      ]
    });

    // Mark form as ready - this will show the form in the template
    this.isFormReady = true;

    // Force change detection after a short delay to ensure values are visible
    setTimeout(() => {
      console.log('Form value after timeout:', this.entry.value);
      Object.keys(languageFields).forEach(key => {
        console.log(`Control [${key}] value:`, this.entry.get(key)?.value);
      });
    }, 100);
  }

  public onSubmit(): void {
    if (this.entry.valid && !this.isButtonsDisabled) {
      this.isButtonsDisabled = true;
      const value = this.entry.value;

      // Find existing relationship
      const existingRelationship = this.referenceRelationships.find(
        (rr: IReferenceRelationship) => rr.value === this.relationshipValue
      );

      // Build relationship_languages array from form values
      const relationship_languages = this.languages
        .map((lang: any) => {
          const label = value[lang.id];
          if (!label || label.trim() === '') {
            return null;
          }

          // Find existing language entry
          let existingLang: any = null;
          if (existingRelationship?.relationship_languages) {
            existingLang = existingRelationship.relationship_languages.find(
              (rl: any) => rl.code === lang.id
            );
          }

          return {
            id: existingLang?.id,
            code: lang.id,
            label: label
          };
        })
        .filter((lang) => lang !== null);

      const relationshipData: any = {
        id: existingRelationship?.id || undefined,
        value: this.relationshipValue,
        hiring_firm: this.hiringFirm,
        prevent_personal_emails: value.prevent_personal_emails,
        is_active: true,
        relationship_languages
      };

      // Include question set selection (null id signals unlinking)
      relationshipData.question_set = value.question_set_id ? { id: value.question_set_id } : { id: null };

      const method = existingRelationship?.id ? 'patch' : 'post';
      const url = existingRelationship?.id
        ? `PATCH /hiring_firms/${this.hiringFirm.id}/reference_relationships/${existingRelationship.id}`
        : `POST /hiring_firms/${this.hiringFirm.id}/reference_relationships`;

      (this._referenceRelationships as any)[method](
        new ReferenceRelationship(relationshipData).apiData,
        this.hiringFirm.id
      ).subscribe(
        (res: any) => {
          this._logger.info(this.constructorName, url, res);

          // Check if response contains data (successful update)
          if (res && res.id) {
            // Update the session storage with new/updated relationship
            if (existingRelationship) {
              // Update existing
              const index = this.referenceRelationships.findIndex(
                (rr) => rr.id === existingRelationship.id
              );
              if (index > -1) {
                this.referenceRelationships[index] = res;
              }
            } else {
              // Add new
              this.referenceRelationships.push(res);
            }

            // Show success message
            const message = existingRelationship?.id
              ? 'TOASTS.RELATIONSHIP_UPDATED'
              : 'TOASTS.RELATIONSHIP_CREATED';
            this._toast.success(message);

            // Close the modal
            this.bsModalRef.hide();

            // Reload the page to reflect changes
            setTimeout(() => {
              window.location.reload();
            }, 500);
          } else {
            // Unexpected response format
            this._logger.error(this.constructorName, 'Unexpected response format', res);
            this._toast.error('TOASTS.UNEXPECTED_RESPONSE');
          }
        },
        (err: any) => {
          this._logger.error(this.constructorName, url, err);

          // Extract error message from response
          let errorMessage = 'TOASTS.RELATIONSHIP_SAVE_FAILED';
          if (err?.error?.errors && Array.isArray(err.error.errors)) {
            // Handle validation errors from Rails
            const errorMessages = err.error.errors.map((e: any) => e.detail || e.title).filter(Boolean);
            if (errorMessages.length > 0) {
              errorMessage = errorMessages.join(', ');
            }
          } else if (err?.error?.message) {
            errorMessage = err.error.message;
          } else if (err?.message) {
            errorMessage = err.message;
          }

          this._toast.error(errorMessage);
          this.isButtonsDisabled = false;
        }
      );
    }
  }

  public closeModal(): void {
    this.bsModalRef.hide();
  }

  public hasLanguage(languageId: string): boolean {
    return this.languages.some((l: any) => l.id === languageId);
  }

  protected createForm() {
    this.entry = this._fb.group({});
  }

  private loadQuestionSets() {
    this._logger.info(this.constructorName, 'Starting to load question sets', {});

    // Filter to only show Reference type question sets
    const filters = {
      filter: {
        key: 'qs',
        value: 'scope:reference'
      }
    };

    this._questionSets
      .get(filters)
      .subscribe(
        (res: IQuestionSet[]) => {
          this.questionSets = res || [];

          this._logger.info(
            this.constructorName,
            `Loaded ${this.questionSets.length} reference type question sets`,
            this.questionSets
          );

          if (this.questionSets.length === 0) {
            console.warn('No reference type question sets found!');
            this._logger.info(
              this.constructorName,
              'No reference type question sets found for this hiring firm',
              {}
            );
          } else {

          }

          // Build the form now that question sets are loaded
          this.buildForm();
        },
        (err: any) => {
          console.error('Question sets loading error:', err);
          this._logger.error(
            this.constructorName,
            'Error loading question sets',
            err
          );
          this._toast.error('TOASTS.QUESTION_SETS_LOAD_FAILED', undefined, 'TOASTS.ERROR');
          // Build form anyway, even if question sets fail to load
          this.buildForm();
        }
      );
  }
}
